import { useState, useRef, useEffect, useCallback } from "react";
import { t, font } from "@realtyflows/ui";
import RioAvatar from "./RioAvatar";
import TypingDots from "./TypingDots";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001";

function renderMarkdown(text) {
  if (!text) return "";
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" style="color:#2D5AA0;text-decoration:underline">$1</a>')
    .replace(/^- (.+)$/gm, "<li>$1</li>")
    .replace(/(<li>.*<\/li>)/s, "<ul style='margin:4px 0;padding-left:16px'>$1</ul>")
    .replace(/\n/g, "<br/>");
}

export default function AIAssistant({ token, onClose }) {
  const [messages, setMessages] = useState(() => {
    try { const saved = localStorage.getItem("rio_agent_messages"); return saved ? JSON.parse(saved) : []; } catch { return []; }
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem("rio_agent_session") || null; } catch { return null; }
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Save to localStorage
  useEffect(() => {
    try { localStorage.setItem("rio_agent_messages", JSON.stringify(messages)); } catch {}
  }, [messages]);
  useEffect(() => {
    if (sessionId) try { localStorage.setItem("rio_agent_session", sessionId); } catch {}
  }, [sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{ role: "assistant", content: "Hey! I'm **Rio**, your AI assistant. I know your clients, deals, and pipeline. How can I help?", time: Date.now() }]);
    }
    setTimeout(() => inputRef.current?.focus(), 300);
  }, [messages.length]);

  const sendMessage = useCallback(async (overrideMsg) => {
    const msg = overrideMsg || input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg, time: Date.now() }]);
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/chatbot/agent`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sessionId, message: msg }),
      });
      const data = await res.json();
      if (data.sessionId) setSessionId(data.sessionId);
      setMessages(prev => [...prev, { role: "assistant", content: data.message, time: Date.now() }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I'm having trouble connecting. Please try again.", time: Date.now() }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [input, loading, sessionId, token]);

  const quickActions = [
    { label: "Top clients", icon: "👥", msg: "Show me my top clients by score" },
    { label: "Pipeline", icon: "📊", msg: "Give me a summary of my pipeline" },
    { label: "Follow-ups", icon: "📞", msg: "Which clients need follow-up?" },
    { label: "Draft email", icon: "✉️", msg: "Help me draft a follow-up email" },
    { label: "This month", icon: "📅", msg: "What's my performance this month?" },
    { label: "Next steps", icon: "🎯", msg: "What should I focus on today?" },
  ];

  const formatTime = (ts) => new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return (
    <div style={{
      position: "fixed", bottom: 90, right: 24, width: 360, height: "min(520px, calc(100vh - 120px))",
      borderRadius: 20, overflow: "hidden",
      background: "#fff",
      display: "flex", flexDirection: "column", zIndex: 1000,
      boxShadow: "0 12px 48px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)",
      animation: "assistantSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      <style>{`@keyframes assistantSlideUp { from { opacity:0; transform:translateY(20px) scale(0.95); } to { opacity:1; transform:translateY(0) scale(1); } }`}</style>

      {/* Header */}
      <div style={{
        padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "linear-gradient(135deg, #1A3260, #2D5AA0)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <RioAvatar size={38} animated />
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", fontFamily: font.display }}>Rio</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", fontFamily: font.body }}>Your AI Assistant</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { setMessages([]); setSessionId(null); try { localStorage.removeItem("rio_agent_messages"); localStorage.removeItem("rio_agent_session"); } catch {} }} title="New chat" style={{
            background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", width: 30, height: 30,
            borderRadius: 8, cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center",
          }}>&#8635;</button>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", width: 30, height: 30,
            borderRadius: 8, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center",
          }}>&times;</button>
        </div>
      </div>

      {/* Quick actions */}
      {messages.length <= 1 && (
        <div style={{ padding: "10px 14px", borderBottom: "1px solid #f1f5f9", background: "#fafbfc" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {quickActions.map(a => (
              <button key={a.label} onClick={() => sendMessage(a.msg)} style={{
                padding: "5px 10px", borderRadius: 20, border: "1px solid #e2e8f0",
                background: "#fff", fontSize: 10, color: "#475569", cursor: "pointer",
                fontFamily: font.body, transition: "all 0.15s",
                display: "flex", alignItems: "center", gap: 4,
              }}
                onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
              >
                <span style={{ fontSize: 12 }}>{a.icon}</span> {a.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px 8px", background: "#f8f9fb" }}>
        {messages.map((msg, i) => (
          <div key={i} style={{
            display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
            marginBottom: 14, gap: 8, alignItems: "flex-end",
          }}>
            {msg.role === "assistant" && <RioAvatar size={30} />}
            <div>
              <div style={{
                maxWidth: 280, padding: "10px 14px", borderRadius: 16,
                background: msg.role === "user" ? "linear-gradient(135deg, #1A3260, #2D5AA0)" : "#fff",
                color: msg.role === "user" ? "#fff" : t.textPrimary,
                fontSize: 13, fontFamily: font.body, lineHeight: 1.6,
                borderBottomRightRadius: msg.role === "user" ? 4 : 16,
                borderBottomLeftRadius: msg.role === "assistant" ? 4 : 16,
                boxShadow: msg.role === "assistant" ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
                whiteSpace: "pre-wrap",
              }} dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
              <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 3, textAlign: msg.role === "user" ? "right" : "left", fontFamily: font.mono }}>
                {msg.time ? formatTime(msg.time) : ""}
              </div>
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 14 }}>
            <RioAvatar size={30} speaking />
            <div style={{ padding: "10px 14px", borderRadius: 16, background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <TypingDots />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{ padding: "14px 16px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", gap: 8 }}>
        <input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Ask Rio anything..."
          disabled={loading}
          style={{
            flex: 1, padding: "10px 14px", borderRadius: 12, border: "1px solid #e2e8f0",
            background: "#f8f9fb", fontSize: 13, fontFamily: font.body, color: t.textPrimary, outline: "none",
            transition: "border-color 0.2s",
          }}
          onFocus={e => e.target.style.borderColor = "#2D5AA0"}
          onBlur={e => e.target.style.borderColor = "#e2e8f0"}
        />
        <button onClick={() => sendMessage()} disabled={loading || !input.trim()} style={{
          width: 42, height: 42, borderRadius: 12, border: "none",
          background: loading || !input.trim() ? "#e2e8f0" : "linear-gradient(135deg, #1A3260, #2D5AA0)",
          color: "#fff", cursor: loading ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.2s",
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </button>
      </div>
    </div>
  );
}
